import { useEffect } from 'react';
import { useRequiredParams, usePdfSearch } from 'app/router/params';

import { Editor } from 'app/components/editor/Editor';
import withMentions from 'slate/extensions/mentions';
import withNotes from 'slate/extensions/notes';
import withPage from 'slate/extensions/page';
import withParagraph from 'slate/extensions/paragraph';
import withLayout from 'slate/extensions/layout';
import withImage from 'slate/extensions/image';
import withImportArea from 'slate/extensions/import-area';
import withReferenceTable from 'slate/extensions/reference-table';
import { useGetDocument, SectionEntry } from 'entities/document';
import { useUser } from 'app/state/contexts/UserContext';
import { mapLanguages } from 'app/utils/translations';
import withTableOfContent from 'slate/extensions/table-of-content';
import withBookmarks from 'slate/extensions/bookmark';
import withLinks from 'slate/extensions/link';
import withStyledParagraph from 'slate/extensions/styled-paragraph';
import withTag from 'slate/extensions/tag';
import { TagContextProvider } from 'app/state/contexts/TagContext';
import { EditorModeContextProvider } from 'app/state/contexts/EditorContext';

const extensions = [
  withLayout({ markEmpty: false }),
  withMentions,
  withImportArea({ readOnly: true }),
  withImage({ readOnly: true }),
  withPage({ withButtons: false, readOnly: true }),
  withNotes,
  withParagraph({ withComments: false }),
  withReferenceTable({ readOnly: true }),
  withTableOfContent({ readOnly: true }),
  withBookmarks({}),
  withLinks({}),
  withStyledParagraph({ isPDF: true }),
  withTag({ isPDF: true }),
];

export const PdfViewer = () => {
  const { selectedOrganization } = useUser();

  const { documentId, documentRevisionId } = useRequiredParams('documentId', 'documentRevisionId');
  const pdfSearch = usePdfSearch();
  const useDraft = pdfSearch.useDraft || false;
  const pageBorders = pdfSearch.pageBorders || false;
  const { data: doc } = useGetDocument(documentId, selectedOrganization, documentRevisionId, useDraft);
  const localeRaw = pdfSearch.locale || doc?.content?.locale;
  const locale = localeRaw !== '' ? localeRaw : 'sv-SE';

  useEffect(() => {
    // TODO Make it so that we can render correct instead of this hack
    // When viewing the "pdf" no styles from the tool should be present
    ['head style:not([data-xrp-keep="true"])', 'head link:not([data-xrp-keep="true"])'].forEach((query) =>
      document.querySelectorAll(query).forEach((n) => n.remove())
    );
  });

  if (!doc) {
    return null;
  }

  return (
    <EditorModeContextProvider>
      <TagContextProvider>
        <div className="pdf-viewer">
          {doc.content.sections.map((section: SectionEntry) => (
            <Editor
              key={section.superId}
              extensions={extensions}
              withShadowDom={false}
              readOnly
              instance={{
                documentId: doc.superId,
                documentRevisionId: doc.revisionId,
                sectionId: section.superId,
                translationId:
                  mapLanguages(doc as Parameters<typeof mapLanguages>[0], section.superId).find((l) => l.locale === locale)?.superId || section.superId,
              }}
              showErrors={false}
              pageBorders={pageBorders}
            >
              {' '}
            </Editor>
          ))}
        </div>
      </TagContextProvider>
    </EditorModeContextProvider>
  );
};
